// Screen to Permission Mapping
// Each screen can have one or more required permissions
// User needs AT LEAST ONE of the listed permissions to access the screen

const SCREEN_PERMISSIONS = {
  // Dashboard - Everyone can access
  'dashboard': [],
  
  // Inventory Management
  'inventory': ['view_inventory'],
  'overview': ['view_inventory'],
  'inventory-stock-out': ['view_inventory', 'stock_out'],
  'stock-in': ['stock_in'],
  'stock-in-scan': ['stock_in'],
  'bill-memory': ['stock_in', 'manage_inventory'], // Settings › Bill Memory: stock_in reads, manage_inventory edits (enforced by the server)
  'stock-out': ['stock_out'],
  'vendors': ['view_vendors'],
  'wastage': ['view_inventory'],
  'usage': ['view_inventory'],
  'xyz': ['view_inventory'],
  'minimal-stock': ['view_inventory'],
  'create-reorder': ['view_vendors', 'stock_in'],
  'inventory-calculator': ['view_inventory'],
  'wastage-thresholds': ['view_inventory'],
  'expiry-thresholds': ['view_inventory'],
  'ocr': ['stock_in'],
  
  // Menu Management
  'menu': ['view_menu'],
  'categories': ['view_menu'],
  'recipe-library': ['view_menu'],
  'itemmap': ['view_menu'],
  'qr-itemmap': ['view_menu'],
  'customization-mapping': ['view_menu'],
  'vid-manager': ['view_inventory'],
  'expiry-control': ['view_inventory'],
  
  // Orders
  'orders': ['view_orders'],
  'kitchen': ['view_orders', 'manage_orders'],
  'chef-dashboard': ['view_orders', 'manage_orders'],
  'waiter-dashboard': ['view_orders'],
  'waiter-pov': ['view_orders'],
  
  // Tables
  'tables': ['view_tables'],
  'table-qr': ['view_tables'],
  'qr-tables': ['view_tables'],
  'qr-table-config': [], // Owner can always edit table configuration
  
  // Staff Management
  'waiters': ['view_staff'],
  'waiter-calls': ['view_staff', 'view_orders'],
  
  // Customers
  'customers': ['view_customers'],
  'customer-menu': [], // Public - accessible via QR code
  'customer-menu-preview': [], // Public - preview mode
  
  // Analytics & Reports
  'analytics': ['view_reports'],
  'sales-report': ['view_reports'],
  'sales-eom': ['view_reports'],
  'wastage-eom': ['view_reports'],
  'expense-eom': ['view_reports'],
  'leakage-eom': ['view_reports'],
  // The banquet month: its own report, not one of the EOMs above.
  'banquet-eom': ['view_reports'],
  // Monthly leakage: four screens, one permission. They are read-only reports
  // plus the actions that follow from them, all gated the same way.
  'leakage-monthly': ['view_reports'],
  'leakage-monthly-category': ['view_reports'],
  'leakage-monthly-item': ['view_reports'],
  'leakage-monthly-history': ['view_reports'],
  'leakage-prep-yield': ['view_reports'],
  'leakage-mapping-integrity': ['view_reports'],
  // 'leakage-a-tier' is deliberately absent. It WRITES a closing-stock row, so
  // App.js gates it with getScreenPermissions('closing-stock') — whatever that
  // screen requires, this one requires, today and after anyone changes it.
  // Naming a permission of its own here would have invented a requirement no
  // role actually holds and locked the sheet away from everyone.
  'reports': ['view_reports'],
  'todays-sales-report': ['view_reports', 'manage_sales'],
  'financial-dashboard': ['view_reports'],
  'qr-analytics': ['view_reports'],
  
  // QR Billing System
  'qr-owner-dashboard': [],
  'qr-menu-management': ['view_menu'],
  'qr-billing-onboarding': [],
  'qr-billing-onboarding-edit': [],
  
  // Business Management & Settings
  'qr-management': ['view_settings'],
  'business-hours': ['view_settings'],
  'business-settings': ['view_settings'],
  'settings': ['view_settings'],
  'users': ['view_settings'],
  'profile': [], // Everyone can access their profile
  
  // Unit Mapping & Setup (Settings related)
  'map': ['view_settings'],
  'map1': ['view_settings'],
  'map2': ['view_settings'],
  'map3': ['view_settings'],
  'initial-recovery': ['view_settings'],
  
  // Production
  'production': ['view_production'],
  'production-plan': ['view_production'],
  'production-recipes': ['view_production'],
  
  // Notifications
  'notifications': [],
  
  // Testing & Development
  'test': [], // Development only
  'service-test': [], // Development only
  'test-medium-features': [], // Development only
  
  // Public/Auth Pages
  'onboarding': [], // Public - no permissions required
  'login': [], // Public
  'signup': [], // Public
  'welcome': [], // Public
  'forgot': [], // Public - forgot password
  'forgot-password': [], // Public
  'reset-password': [], // Public
};

/**
 * Get required permissions for a screen
 * @param {string} screenName - The screen identifier
 * @returns {string[]} - Array of required permissions (empty = no restrictions)
 */
export const getScreenPermissions = (screenName) => {
  return SCREEN_PERMISSIONS[screenName] || [];
};

/**
 * Check if a screen requires any permissions
 * @param {string} screenName - The screen identifier
 * @returns {boolean} - True if screen has permission requirements
 */
export const isProtectedScreen = (screenName) => {
  const permissions = getScreenPermissions(screenName);
  return permissions && permissions.length > 0;
};

/**
 * Get all public screens (no permission required)
 * @returns {string[]} - Array of public screen names
 */
export const getPublicScreens = () => {
  return Object.keys(SCREEN_PERMISSIONS).filter(screen => {
    const perms = SCREEN_PERMISSIONS[screen];
    return !perms || perms.length === 0;
  });
};

/**
 * Get all protected screens
 * @returns {Object} - Object with screen names as keys and required permissions as values
 */
export const getProtectedScreens = () => {
  const protectedScreens = {};
  Object.keys(SCREEN_PERMISSIONS).forEach(screen => {
    const perms = SCREEN_PERMISSIONS[screen];
    if (perms && perms.length > 0) {
      protectedScreens[screen] = perms;
    }
  });
  return protectedScreens;
};

export default SCREEN_PERMISSIONS;
